import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  CheckCircle2,
  Clock,
  Target,
  ArrowUpRight,
  FileText,
  ExternalLink,
  Play,
  CheckCheck,
  AlertCircle,
  TrendingUp,
  Building2,
  Globe,
  TriangleAlert,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { api, useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const STAT_COLOR_CLASSES = {
  indigo:  { bg: 'bg-indigo-100 dark:bg-indigo-900/30',  text: 'text-indigo-600' },
  amber:   { bg: 'bg-amber-100 dark:bg-amber-900/30',    text: 'text-amber-600' },
  violet:  { bg: 'bg-violet-100 dark:bg-violet-900/30',  text: 'text-violet-600' },
  cyan:    { bg: 'bg-cyan-100 dark:bg-cyan-900/30',      text: 'text-cyan-600' },
  emerald: { bg: 'bg-emerald-100 dark:bg-emerald-900/30',text: 'text-emerald-600' },
};

const StatCard = ({ icon: Icon, label, value, subValue, color = 'indigo' }) => {
  const colors = STAT_COLOR_CLASSES[color] || STAT_COLOR_CLASSES.indigo;
  return (
    <Card className="gds-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl ${colors.bg} flex items-center justify-center`}>
            <Icon className={`w-6 h-6 ${colors.text}`} />
          </div>
          <div>
            <p className="text-2xl font-bold font-heading">{value}</p>
            <p className="text-sm text-slate-600">{label}</p>
            {subValue && <p className="text-xs text-slate-400 mt-1">{subValue}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ClientCard = ({ client, onViewTasks }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="p-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:shadow-md transition-all"
  >
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
          <Building2 className="w-5 h-5 text-indigo-600" />
        </div>
        <div>
          <h3 className="font-semibold">{client.name}</h3>
          <p className="text-sm text-slate-500">{client.company_name || 'No company'}</p>
        </div>
      </div>
      <Badge variant={client.onboarding_complete ? 'default' : 'secondary'}>
        {client.onboarding_complete ? 'Active' : 'Onboarding'}
      </Badge>
    </div>
    
    {client.website && (
      <a 
        href={client.website.startsWith('http') ? client.website : `https://${client.website}`}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1 text-sm text-indigo-600 hover:underline mb-3"
      >
        <Globe className="w-4 h-4" />
        {client.website}
        <ExternalLink className="w-3 h-3" />
      </a>
    )}
    
    <div className="space-y-2 mb-3">
      <div className="flex justify-between text-sm">
        <span className="text-slate-600">Task Progress</span>
        <span className="font-medium">{client.tasks_completed}/{client.tasks_total}</span>
      </div>
      <Progress value={client.progress_percentage} className="h-2" />
    </div>
    
    <Button 
      variant="outline" 
      size="sm" 
      className="w-full"
      onClick={() => onViewTasks(client.user_id)}
    >
      View Tasks
      <ArrowUpRight className="w-4 h-4 ml-1" />
    </Button>
  </motion.div>
);

const TaskItem = ({ task, onStart, onComplete }) => {
  const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'done';
  return (
    <div className={`p-4 rounded-lg border transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50 ${
      isOverdue
        ? 'border-red-200 dark:border-red-800 bg-red-50/30 dark:bg-red-900/10'
        : 'border-slate-200 dark:border-slate-700'
    }`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-sm">{task.title}</h4>
            {isOverdue && (
              <span className="flex items-center gap-1 text-xs text-red-600 font-medium">
                <TriangleAlert className="w-3 h-3" />
                Overdue
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-1 line-clamp-2">{task.description}</p>
        </div>
        <Badge
          variant={
            task.status === 'done' ? 'success' :
            task.status === 'in_progress' ? 'warning' :
            'secondary'
          }
          className="ml-2"
        >
          {task.status?.replace('_', ' ')}
        </Badge>
      </div>

      <div className="flex items-center justify-between mt-3">
        <span className={`text-xs ${isOverdue ? 'text-red-500 font-medium' : 'text-slate-400'}`}>
          {task.due_date
            ? `Due: ${new Date(task.due_date).toLocaleDateString()}`
            : 'No due date'}
        </span>
        <div className="flex gap-2">
          {task.status === 'todo' && (
            <Button size="sm" variant="outline" onClick={() => onStart(task.task_id)}>
              <Play className="w-3 h-3 mr-1" />
              Start
            </Button>
          )}
          {task.status === 'in_progress' && (
            <Button size="sm" className="gds-btn-primary" onClick={() => onComplete(task.task_id)}>
              <CheckCheck className="w-3 h-3 mr-1" />
              Complete
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default function ExecutionDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [completedThisWeek, setCompletedThisWeek] = useState(0);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientTasks, setClientTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [activeTab, setActiveTab] = useState('tasks');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [dashRes, doneRes] = await Promise.allSettled([
        api.get('/execution/dashboard'),
        api.get('/tasks', { params: { status: 'done' } }),
      ]);

      if (dashRes.status === 'fulfilled') {
        setDashboardData(dashRes.value.data);
      } else {
        toast.error('Failed to load dashboard data');
      }

      if (doneRes.status === 'fulfilled') {
        const doneTasks = Array.isArray(doneRes.value.data)
          ? doneRes.value.data
          : (doneRes.value.data?.items || []);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        const thisWeek = doneTasks.filter(t => t.updated_at && new Date(t.updated_at) >= weekAgo);
        setCompletedThisWeek(thisWeek.length);
      }
    } catch (error) {
      console.error('Error fetching execution dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewTasks = async (clientId) => {
    setSelectedClient(clientId);
    setActiveTab('client-tasks');
    setLoadingTasks(true);
    try {
      const response = await api.get(`/execution/client/${clientId}/tasks`);
      setClientTasks(response.data);
    } catch (error) {
      console.error('Error fetching client tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoadingTasks(false);
    }
  };

  const handleStartTask = async (taskId) => {
    try {
      await api.post(`/execution/task/${taskId}/start`);
      toast.success('Task started');
      // Refresh tasks
      if (selectedClient) {
        handleViewTasks(selectedClient);
      }
      fetchDashboardData();
    } catch (error) {
      console.error('Error starting task:', error);
      toast.error('Failed to start task');
    }
  };

  const handlePublishContent = async (contentId) => {
    try {
      await api.post(`/content/${contentId}/publish`);
      toast.success('Content marked as published');
      fetchDashboardData();
    } catch (error) {
      console.error('Error publishing content:', error);
      toast.error(error?.response?.data?.detail || 'Failed to publish content');
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await api.post(`/execution/task/${taskId}/complete`, { notes: 'Completed by execution team' });
      toast.success('Task completed');
      // Refresh tasks
      if (selectedClient) {
        handleViewTasks(selectedClient);
      }
      fetchDashboardData();
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error('Failed to complete task');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  const summary = dashboardData?.summary || {};
  const clients = dashboardData?.clients || [];
  const approvedContent = dashboardData?.approved_content || [];
  const approvedOnpage = dashboardData?.approved_onpage_recommendations || [];

  const now = new Date();
  const approvedTasks = [...(dashboardData?.approved_tasks || [])].sort((a, b) => {
    const aOverdue = a.due_date && new Date(a.due_date) < now;
    const bOverdue = b.due_date && new Date(b.due_date) < now;
    if (aOverdue && !bOverdue) return -1;
    if (!aOverdue && bOverdue) return 1;
    if (!a.due_date) return 1;
    if (!b.due_date) return -1;
    return new Date(a.due_date) - new Date(b.due_date);
  });

  return (
    <div className="space-y-8" data-testid="execution-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-1">
            Execution Dashboard
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Manage client work, approved tasks, and implementations
          </p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          icon={Users} 
          label="Total Clients" 
          value={summary.total_clients || 0}
          color="indigo"
        />
        <StatCard 
          icon={Clock} 
          label="Active Tasks" 
          value={summary.active_tasks || 0}
          color="amber"
        />
        <StatCard 
          icon={Target} 
          label="Pending Implementations" 
          value={summary.pending_implementations || 0}
          color="violet"
        />
        <StatCard
          icon={CheckCircle2}
          label="Completed This Week"
          value={completedThisWeek}
          color="emerald"
        />
      </div>

      {/* Main Content */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Clients List */}
        <Card className="lg:col-span-1 gds-card">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-500" />
              All Clients
            </CardTitle>
            <CardDescription>
              {clients.length} active client{clients.length !== 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[500px] pr-4">
              <div className="space-y-4">
                {clients.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>No clients yet</p>
                  </div>
                ) : (
                  clients.map((client) => (
                    <ClientCard 
                      key={client.user_id} 
                      client={client} 
                      onViewTasks={handleViewTasks}
                    />
                  ))
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Tasks & Work Items */}
        <Card className="lg:col-span-2 gds-card">
          <CardHeader>
            <CardTitle className="font-heading">Work Queue</CardTitle>
            <CardDescription>
              Approved items awaiting execution
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="tasks">
                  Tasks ({approvedTasks.length})
                </TabsTrigger>
                <TabsTrigger value="onpage">
                  On-Page SEO ({approvedOnpage.length})
                </TabsTrigger>
                <TabsTrigger value="content">
                  Content ({approvedContent.length})
                </TabsTrigger>
                {selectedClient && (
                  <TabsTrigger value="client-tasks">
                    Client Tasks ({clientTasks.length})
                  </TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="tasks">
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {approvedTasks.length === 0 ? (
                      <div className="text-center py-12 text-slate-500">
                        <CheckCircle2 className="w-12 h-12 mx-auto mb-2 opacity-30" />
                        <p>No pending tasks</p>
                        <p className="text-xs mt-1">All caught up!</p>
                      </div>
                    ) : (
                      approvedTasks.map((task) => (
                        <TaskItem 
                          key={task.task_id} 
                          task={task}
                          onStart={handleStartTask}
                          onComplete={handleCompleteTask}
                        />
                      ))
                    )}
                  </div>
                </ScrollArea>
              </TabsContent>

              <TabsContent value="onpage">
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {approvedOnpage.length === 0 ? (
                      <div className="text-center py-12 text-slate-500">
                        <Target className="w-12 h-12 mx-auto mb-2 opacity-30" />
                        <p>No pending implementations</p>
                      </div>
                    ) : (
                      approvedOnpage.map((item) => (
                        <div 
                          key={item.recommendation_id}
                          className="p-4 rounded-lg border border-slate-200 dark:border-slate-700"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <Badge variant="outline" className="mb-2">{item.category}</Badge>
                              <h4 className="font-medium text-sm">{item.title}</h4>
                              <p className="text-xs text-slate-500 mt-1">{item.description}</p>
                            </div>
                            <Badge variant={item.severity === 'high' ? 'destructive' : 'secondary'}>
                              {item.severity}
                            </Badge>
                          </div>
                          {item.code_snippet && (
                            <pre className="mt-2 p-2 bg-slate-100 dark:bg-slate-800 rounded text-xs overflow-x-auto">
                              {item.code_snippet}
                            </pre>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </TabsContent>

              <TabsContent value="content">
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {approvedContent.length === 0 ? (
                      <div className="text-center py-12 text-slate-500">
                        <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
                        <p>No content to publish</p>
                      </div>
                    ) : (
                      approvedContent.map((content) => (
                        <div 
                          key={content.content_id}
                          className="p-4 rounded-lg border border-slate-200 dark:border-slate-700"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-medium text-sm">{content.title}</h4>
                              <p className="text-xs text-slate-500 mt-1">
                                Type: {content.content_type}
                              </p>
                            </div>
                            <Button size="sm" variant="outline" onClick={() => handlePublishContent(content.content_id)}>
                              Publish
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </TabsContent>

              {selectedClient && (
                <TabsContent value="client-tasks">
                  <ScrollArea className="h-[400px]">
                    {loadingTasks ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {clientTasks.length === 0 ? (
                          <div className="text-center py-12 text-slate-500">
                            <CheckCircle2 className="w-12 h-12 mx-auto mb-2 opacity-30" />
                            <p>No tasks for this client</p>
                          </div>
                        ) : (
                          clientTasks.map((task) => (
                            <TaskItem 
                              key={task.task_id} 
                              task={task}
                              onStart={handleStartTask}
                              onComplete={handleCompleteTask}
                            />
                          ))
                        )}
                      </div>
                    )}
                  </ScrollArea>
                </TabsContent>
              )}
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
